import { Rocket } from "lucide-react";

import "./captain.css";
import { NebulaBackground } from "./nebula-background";
import { defineTheme } from "../types";

/**
 * Captain — the look of the Falcon marketing site (falconplatform.onlineresults.nl) and brand
 * styleguide (falcon-styleguide.vercel.app): near-black ink, translucent white surfaces and
 * hairlines, Signal blue as the only functional accent. Dark-based, so every dark: variant and
 * every token left out here keep their .dark value. Literal values carry the styleguide hex they
 * were converted from. Glass, the launch button, chrome hairlines and the starfield are in
 * captain.css.
 */
export const captain = defineTheme({
  id: "captain",
  label: "Captain",
  icon: Rocket,
  base: "dark",
  tokens: {
    // The site's UI grays are achromatic, not the 232deg gunmetal the neutral ramp is built on.
    // Primary needs no retune: the primary ramp already is the styleguide's Signal family
    // (600 = Signal #1BAAE2, 500/400/300 = launch-gradient stops, 950 = Signal foreground #03151C).
    "chroma-neutral": "0",
    background: "oklch(14.48% 0 0deg)", // #0a0a0a
    "background-alt": "oklch(20.46% 0 0deg)", // #171717 — styleguide --background-elevated
    foreground: "oklch(98.51% 0 0deg)", // #fafafa

    // Above the styleguide's 2%: at 2% cards measured ~1.03:1 against the page and disappeared.
    card: "oklch(100% 0 0deg / 5%)",
    "card-foreground": "var(--foreground)",
    popover: "var(--background-alt)", // opaque: popovers float over content
    "popover-foreground": "var(--foreground)",
    primary: "var(--color-primary-600)", // Signal
    "primary-foreground": "var(--color-primary-950)", // Signal foreground
    secondary: "oklch(26.86% 0 0deg)", // #262626 — styleguide --muted
    "secondary-foreground": "var(--foreground)",
    muted: "oklch(26.86% 0 0deg)", // #262626
    "muted-foreground": "oklch(70.9% 0 0deg)", // #a1a1a1 — styleguide --foreground-muted
    accent: "oklch(26.86% 0 0deg)", // #262626
    "accent-foreground": "var(--foreground)",
    destructive: "oklch(66.32% 0.1619 18.9deg)", // #e5636b — styleguide --danger
    "destructive-foreground": "var(--background)", // styleguide --danger-foreground
    success: "oklch(72.63% 0.1323 162.1deg)", // #46c08d
    "success-foreground": "var(--primary-foreground)", // styleguide reuses Signal foreground
    // No warning hue of its own in the styleguide (it aliases Signal): keep dark's amber, with the
    // site's dark ink on bright fills.
    "warning-foreground": "var(--primary-foreground)",

    // Above the styleguide's white/10 hairline: glass cards, tables and fields need the stronger
    // edge to hold against the near-black page.
    border: "oklch(100% 0 0deg / 16%)",
    input: "oklch(100% 0 0deg / 15%)",
    ring: "var(--primary)", // styleguide: Signal marks focus rings

    // The Topbar/TopNavigation fill (bg-background/80): with the backdrop glows painted on <body>,
    // a transparent sidebar would show them while the top bars cover them.
    sidebar: "oklch(from var(--background) l c h / 80%)",
    "sidebar-foreground": "var(--foreground)",
    "sidebar-primary": "var(--primary)",
    "sidebar-primary-foreground": "var(--primary-foreground)",
    "sidebar-accent": "var(--accent)",
    "sidebar-accent-foreground": "var(--foreground)",
    "sidebar-border": "var(--border)",
    "sidebar-ring": "var(--ring)",

    // The site's pill recipe: family tint at 10% fill / 30% border, light 300 step for text;
    // neutral is the site's white/5 + white/20 + white/80 pill.
    "badge-success-text": "var(--color-success-300)",
    "badge-success-bg": "oklch(from var(--success) l c h / 10%)",
    "badge-success-border": "oklch(from var(--success) l c h / 30%)",
    "badge-error-text": "var(--color-destructive-300)",
    "badge-error-bg": "oklch(from var(--destructive) l c h / 10%)",
    "badge-error-border": "oklch(from var(--destructive) l c h / 30%)",
    "badge-info-text": "var(--color-primary-300)",
    "badge-info-bg": "oklch(from var(--color-primary-500) l c h / 10%)",
    "badge-info-border": "oklch(from var(--color-primary-500) l c h / 30%)",
    "badge-warning-text": "var(--color-warning-300)",
    "badge-warning-bg": "oklch(from var(--warning) l c h / 10%)",
    "badge-warning-border": "oklch(from var(--warning) l c h / 30%)",
    "badge-neutral-text": "oklch(100% 0 0deg / 80%)",
    "badge-neutral-bg": "oklch(100% 0 0deg / 5%)",
    "badge-neutral-border": "oklch(100% 0 0deg / 20%)",
    "badge-archive-text": "var(--color-archive-300)",
    "badge-archive-bg": "oklch(from var(--archive) l c h / 10%)",
    "badge-archive-border": "oklch(from var(--archive) l c h / 30%)",

    info: "oklch(58.96% 0.0533 233deg)", // #5d8398 — styleguide --info
    "info-foreground": "var(--foreground)",
    overlay: "oklch(14.48% 0 0deg / 72%)", // styleguide --backdrop
    "code-bg": "var(--background-alt)",
    "code-foreground": "var(--foreground)",

    // Legacy v1 tables and forms: the v2 Table's glass fill and edge
    "color-neutral-fill-background": "var(--card)",
    "color-neutral-stroke-darkest": "var(--border)",
  },
  backdrops: { auth: NebulaBackground },
});
